import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import useGraphStore from '@/store/graphStore';
import { useLanguage } from '@/context/LanguageContext';
import { chainColors } from '@/data/blockchainMockData';

const SelectionPanel = ({ className }) => {
  const { language } = useLanguage();
  const {
    selectedNodes,
    removeFromSelection,
    clearSelection,
    freezeSelected,
    unfreezeAll,
    toggleNeighborhoodMode,
    neighborhoodMode,
  } = useGraphStore();
  
  const t = {
    selection: language === 'de' ? 'Auswahl' : 'Selection',
    nodes: language === 'de' ? 'Knoten' : 'nodes',
    address: language === 'de' ? 'Adresse' : 'Address',
    balance: language === 'de' ? 'Guthaben' : 'Balance',
    connections: language === 'de' ? 'Verbindungen' : 'Connections',
    actions: language === 'de' ? 'Aktionen' : 'Actions',
    expandNeighborhood: language === 'de' ? 'Nachbarschaft erweitern' : 'Expand Neighborhood',
    freezePosition: language === 'de' ? 'Position fixieren' : 'Freeze Position',
    unfreezeAll: language === 'de' ? 'Alle freigeben' : 'Unfreeze All',
    clearSelection: language === 'de' ? 'Auswahl aufheben' : 'Clear Selection',
    remove: language === 'de' ? 'Entfernen' : 'Remove',
    multiSelectHint: language === 'de' ? 'Shift+Klick für Mehrfachauswahl' : 'Shift+click for multi-select',
    lassoHint: language === 'de' ? 'Shift+Ziehen für Lasso' : 'Shift+drag for lasso',
  };
  
  if (selectedNodes.length === 0) {
    return (
      <motion.div
        initial={{ opacity: 0, x: 20 }}
        animate={{ opacity: 1, x: 0 }}
        className={cn("bg-card/95 backdrop-blur border border-border rounded-lg p-4", className)}
      >
        <p className="text-sm text-muted-foreground text-center">
          <i className="fa-solid fa-hand-pointer mb-2 block text-lg"></i>
          {t.multiSelectHint}
          <br />
          <span className="text-xs">{t.lassoHint}</span>
        </p>
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      className={cn("bg-card/95 backdrop-blur border border-border rounded-lg overflow-hidden", className)}
    >
      {/* Header */}
      <div className="p-3 border-b border-border bg-gradient-to-r from-primary/10 to-purple-500/10">
        <div className="flex items-center justify-between">
          <h4 className="font-medium text-sm flex items-center gap-2">
            <motion.div
              animate={{ scale: [1, 1.2, 1] }}
              transition={{ repeat: Infinity, duration: 2 }}
              className="w-2 h-2 rounded-full bg-primary"
            />
            {t.selection}
            <Badge variant="secondary" className="text-xs">
              {selectedNodes.length} {t.nodes}
            </Badge>
          </h4>
          <Button variant="ghost" size="icon" className="h-6 w-6" onClick={clearSelection}>
            <i className="fa-solid fa-xmark text-xs"></i>
          </Button>
        </div>
      </div>
      
      {/* Node List */}
      <ScrollArea className="h-48">
        <div className="p-2 space-y-2">
          <AnimatePresence>
            {selectedNodes.map((node, index) => (
              <motion.div
                key={node.id}
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, x: 20 }}
                transition={{ delay: index * 0.05 }}
                className="flex items-center gap-2 p-2 rounded-lg bg-muted/50 hover:bg-muted transition-colors group"
              >
                <div
                  className="w-3 h-3 rounded-full shrink-0"
                  style={{ backgroundColor: chainColors[node.chain] || node.color }}
                />
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-medium truncate">{node.label}</p>
                  <p className="text-[10px] text-muted-foreground font-mono truncate">
                    {node.address.slice(0, 8)}...{node.address.slice(-6)}
                  </p>
                </div>
                <div className="text-right shrink-0">
                  <p className="text-xs font-mono">{node.balance.toFixed(2)}</p>
                  <p className="text-[10px] text-muted-foreground">{node.degree} conn</p>
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-6 w-6 opacity-0 group-hover:opacity-100 transition-opacity"
                  onClick={() => removeFromSelection(node.id)}
                >
                  <i className="fa-solid fa-times text-xs"></i>
                </Button>
              </motion.div>
            ))}
          </AnimatePresence>
        </div>
      </ScrollArea>
      
      {/* Actions */}
      <div className="p-3 border-t border-border space-y-2">
        <p className="text-xs text-muted-foreground mb-2">{t.actions}</p>
        <div className="grid grid-cols-2 gap-2">
          <Button
            variant="outline"
            size="sm"
            className="text-xs h-8"
            onClick={toggleNeighborhoodMode}
          >
            <i className="fa-solid fa-bullseye mr-1.5"></i>
            {neighborhoodMode ? 'Exit' : t.expandNeighborhood.split(' ')[0]}
          </Button>
          <Button
            variant="outline"
            size="sm"
            className="text-xs h-8"
            onClick={freezeSelected}
          >
            <i className="fa-solid fa-lock mr-1.5"></i>
            {t.freezePosition.split(' ')[0]}
          </Button>
          <Button
            variant="outline"
            size="sm"
            className="text-xs h-8"
            onClick={unfreezeAll}
          >
            <i className="fa-solid fa-unlock mr-1.5"></i>
            {t.unfreezeAll.split(' ')[0]}
          </Button>
          <Button
            variant="outline"
            size="sm"
            className="text-xs h-8"
            onClick={clearSelection}
          >
            <i className="fa-solid fa-eraser mr-1.5"></i>
            {t.clearSelection.split(' ')[0]}
          </Button>
        </div>
      </div>
    </motion.div>
  );
};

export default SelectionPanel;
